/**
 * Shared UI components and types used across scanner views
 * (ToteScannerView, ProductScannerView, ScannerPage).
 * Extracted to avoid circular dependencies between these files.
 */

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Loader from "@amzn/meridian/loader";
import { Button, Icon, Sheet } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import searchTokens from "@amzn/meridian-tokens/base/icon/search";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import PillButton from "src/components/UI/PillButton";
import "src/components/UI/cameraOverlay.scss";
import "./classification.scss";
import { ItemFlowContext } from "src/types/containerTypes";

export const SCAN_SHEET_FALLBACK_MESSAGE = "Something went wrong.";

/** Camera-unavailable message shared by both scanners; `noun` is "tote barcode" or "product ID". */
export const getCameraErrorMessage = (errName: string, noun: string): string => {
  if (errName === "NotAllowedError")
    return `Camera access is blocked. Enable it in your browser settings, or enter the ${noun} manually.`;
  if (errName === "NotFoundError")
    return `No camera found on this device. Enter the ${noun} manually to continue.`;
  return `Camera unavailable. Enter the ${noun} manually to continue.`;
};

interface ScanErrorSheetProps {
  open: boolean;
  message: string | null;
  cameraUnavailable: boolean;
  onClose: () => void;
  onManualEntry: () => void;
  onScanAnother: () => void;
  manualEntryLabel: string;
  scanAnotherLabel: string;
  loading?: boolean;
  loadingMessage?: string;
}

/** Bottom-sheet error/loading pop-up shared by the tote and product scanners. */
export const ScanErrorSheet: React.FC<ScanErrorSheetProps> = ({
  open,
  message,
  cameraUnavailable,
  onClose,
  onManualEntry,
  onScanAnother,
  manualEntryLabel,
  scanAnotherLabel,
  loading = false,
  loadingMessage,
}) => (
  <Sheet type="overlay" side="bottom" open={open} onClose={onClose} spacingInset="400">
    <Column spacingInset="400" alignmentHorizontal="center" spacing="none">
      {loading ? (
        <Row spacingInset="600" alignmentHorizontal="center" alignmentVertical="center">
          <Loader size="large" type="circular" />
          <Text>{loadingMessage}</Text>
        </Row>
      ) : (
        <>
          <Row alignmentHorizontal="end" width="100%">
            <Button onClick={onClose} type="icon" aria-label="Close">
              <Icon tokens={closeSmallTokens} color="primary" />
            </Button>
          </Row>
          <Column alignmentHorizontal="center" spacing="500">
            <Icon tokens={searchTokens} />
            <Text type="h300" alignment="center" role="alert" aria-live="assertive">
              {message ?? SCAN_SHEET_FALLBACK_MESSAGE}
            </Text>
            {cameraUnavailable ? (
              <PillButton text={manualEntryLabel} color="secondary" onClick={onManualEntry} />
            ) : (
              <PillButton text={scanAnotherLabel} color="secondary" onClick={onScanAnother} />
            )}
          </Column>
        </>
      )}
    </Column>
  </Sheet>
);

export enum ScanMode {
  TOTE = "tote",
  PRODUCT = "product",
}

/** Top overlay with back arrow + title — shared across scanner views */
export const ScannerTopOverlay: React.FC<{ title: string; children?: React.ReactNode }> = ({
  title,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const itemFlowContext = (location.state as { itemFlowContext?: ItemFlowContext })
    ?.itemFlowContext;

  const handleBack = () => {
    if (itemFlowContext?.type === "toteExtraItem") {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  return (
    <Column className="camera-overlay camera-overlay--top" spacing="500" spacingInset="500">
      <Row alignmentVertical="center" spacing="500">
        <Button type="icon" onClick={handleBack}>
          <Icon color="inverted" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h200" color="inverted">
          {title}
        </Text>
      </Row>
      {children}
    </Column>
  );
};

export const ScanModeToggle: React.FC<{
  activeMode: ScanMode;
  onModeChange: (mode: ScanMode) => void;
}> = ({ activeMode, onModeChange }) => (
  <Row widths={["fill", "fill"]} className="camera-pill-bg" spacingInset="300">
    <PillButton
      text="Tote"
      color={activeMode === ScanMode.TOTE ? "glass-active" : "glass"}
      onClick={() => onModeChange(ScanMode.TOTE)}
    />
    <PillButton
      text="Product"
      color={activeMode === ScanMode.PRODUCT ? "glass-active" : "glass"}
      onClick={() => onModeChange(ScanMode.PRODUCT)}
    />
  </Row>
);
